import type { z } from 'zod';
import { healthResponseSchema, type HealthResponse } from '@handynest/shared';

// Same-origin by default. In production Firebase Hosting rewrites /api/** to the
// Cloud Run service, so there is no cross-origin request and no API host to configure.
// VITE_API_URL exists only for local development against a separately-run API.
const API_URL = import.meta.env.VITE_API_URL ?? '/api';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Every response is parsed against the shared schema before it reaches a component.
 *
 * This is the payoff for choosing a split API + SPA: the contract in packages/shared is
 * enforced at runtime on the client too, so a backend change that breaks the shape
 * surfaces here with a precise message instead of as `undefined` deep in a render.
 */
async function request<T>(path: string, schema: z.ZodType<T>, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...init?.headers },
  });

  if (!response.ok) {
    throw new ApiError(`Request to ${path} failed`, response.status);
  }

  const parsed = schema.safeParse(await response.json());
  if (!parsed.success) {
    const issues = parsed.error.issues
      .map((issue: z.ZodIssue) => `${issue.path.join('.')} ${issue.message}`)
      .join('; ');
    throw new ApiError(
      `The API returned a response that does not match the agreed contract for ${path}: ${issues}`,
      response.status,
    );
  }
  return parsed.data;
}

export const api = {
  health: (): Promise<HealthResponse> => request('/health', healthResponseSchema),
};
